/**
 * Select Component - HELIX Professional Design System v9.2.1
 *
 * Native select with Executive OS styling.
 * Matches Input component styling for consistency.
 */

import * as React from 'react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
}

type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  placeholder?: string;
  selectSize?: SelectSize;
}

const sizeClasses: Record<SelectSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-[14px] text-sm',
  lg: 'h-10 px-4 text-sm',
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, selectSize = 'md', ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        // Base styles
        'min-w-[180px] rounded-md border border-input bg-secondary',
        // Typography
        'text-foreground',
        // Focus ring
        'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        // Disabled state
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Size
        sizeClasses[selectSize],
        className,
      )}
      {...props}
    >
      {placeholder && (
        <option value="" className="text-muted-foreground">
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});
